import angular from 'angular'
import debounce from 'lodash/debounce';

import { FlexTableContext } from '../../../flex-table'
import {
    CarouselScrollingExtension,
    FixedHeaderExtension,
    FrozenColumnsExtension,
    ExcelHotkeysExtension
} from '../../../flex-table/extensions'

import "./flex-table.directive.less"

/**
 * TODO:
 * 1. Refactor for performance, in particular looking into limiting the data shown on screen (virtual scrolling) and using mutation events
 * 2. Look into graphical glitches resulting from having multiple fixed header rows and a frozen column.
 * @param {any} $compile Angular compile service
 * @param {any} $timeout Angular timeout service
 * @returns {any} The FlexTable directive
 */
export function FlexTableDirective($compile, $timeout) {
    'ngInject'
    const directive = {
        restrict: "A",
        link: link,
        controller: class { },
        controllerAs: "$flex"
    }
    return directive;

    function link(scope, element, attrs) {
        const el = element[0];


        // The directive supports the following options, passed in as strings through attributes:
        // freezeLeft (number) - number of columns on the left to be frozen
        const options = {}
        if (attrs.freezeLeft) {
            options.freezeLeft = Number.parseInt(attrs.freezeLeft)
        }
        // fixedHeaderTop - padding in px on top of the fixed header
        if (attrs.fixedHeaderTop) {
            options.fixedHeaderTop = Number.parseInt(attrs.fixedHeaderTop)
        }

        if (attrs.apexExcelHotkeys) {
            options.enableExcelHotkeys = scope.$eval(attrs.apexExcelHotkeys);
        }

        // verticalScrollHeight - minimum height in pixels required to enable fixed header.
        if (attrs.verticalScrollHeight) {
            options.verticalScrollHeight = Number.parseInt(attrs.verticalScrollHeight)
        }
        // verticalContainer - the container producing the scrollbar that will be checked to
        // determine when the header should be affixed. By default, this is the window.
        options.scrollContainer = getVerticalScrollContainer(el, attrs)

        // Create a new context with options that will carry out the actual logic of the directive.
        const context = new FlexTableContext(el, options)
        scope.$flex.context = context;
        context.extensions = [];
        if (options.freezeLeft) {
            context.extensions.push(
                new FrozenColumnsExtension()
            );
        }
        if (options.verticalScrollHeight) {
            context.extensions.push(
                new FixedHeaderExtension(),
                new CarouselScrollingExtension()
            );
        }

        if (options.enableExcelHotkeys) {
            // Configure the Excel Hotkeys extension.
            configureExcelHotkeys();
        }

        const unsafeDirectives = [
            // ng-repeat expands the DOM; processing these would produce exponentially more results.
            "ng-repeat",
            "ng-repeat-start",
            "ng-repeat-end",
            // processing the directive again would result in infinite recursion
            "apex-freeze-columns",
            "apex-flex-table"
        ]

        // Integrate the table context with the AngularJS compiler.
        context.compilers.push(el => {
            // Delete attributes that would unsafely expand the DOM if reapplied.
            unsafeDirectives.forEach(selectorAttribute => {
                if (el.hasAttribute(selectorAttribute)) {
                    el.removeAttribute(selectorAttribute)
                }
                // Process children using querySelector API.
                Array.from(el.querySelectorAll(`[${selectorAttribute}]`))
                    .forEach(directiveEl => {
                        directiveEl.removeAttribute(selectorAttribute)
                    })
            })

            $compile(angular.element(el))(scope)
            return el;
        })

        // Activate the context.
        $timeout(() => {
            attachToContext(context);

            // Add events to detach/re-attach upon changes to specific variables.
            if (attrs.watchAndRebind) {
                const watchAndRebind = JSON.parse(attrs.watchAndRebind);

                const reAttach = debounce(() => {
                    evaluateAttributeValues();
                    detachFromContext(context)
                    attachToContext(context)
                }, 50)

                for (let i = 0; i < watchAndRebind.length; i++) {
                    scope.$watch(watchAndRebind[i], () => {
                        // Trigger a debounced detach + attach
                        reAttach()
                    })
                }
            }
        })

        // Resize handling: Reattach context on resize
        const handleResize = debounce(() => {
            evaluateAttributeValues();
            detachFromContext(context);
            attachToContext(context);
        }, 100);

        angular.element(window).on('resize', handleResize);

        scope.$on('$destroy', () => {
            detachFromContext(context);
            angular.element(window).off('resize', handleResize);
        });

        function configureExcelHotkeys() {
            let cursor = cursor;
            context.extensions.push(new ExcelHotkeysExtension({
                // Only validate cells that contain an input.
                // inputValidator: el => !!el.querySelector("input,select"),
                getCellValue: cell => {
                    const mapper = context.getCellMapper("excelHotkeys", cell);
                    if (mapper) {
                        return mapper.getValue();
                    }
                    const input = cell.querySelector("input:not([type=hidden]),select");
                    if (input) {
                        return input.value;
                    }
                    return cell.innerText;
                },
                setCellValue: (cell, value) => {
                    const mapper = context.getCellMapper("excelHotkeys", cell);
                    if (mapper) {
                        return mapper.setValue(value);
                    }
                },
                onSelectionChanged: e => {
                    for (const selected of e.selected) {
                        selected.td.classList.add("ehk-cell-selected");
                        const handlers = context.getCellHandlers("excelHotkeys", "select", selected.td);
                        for (const handler of handlers) {
                            handler({ $event: { selected: true } });
                        }
                    }
                    for (const deselected of e.deselected) {
                        deselected.td.classList.remove("ehk-cell-selected");
                        const handlers = context.getCellHandlers("excelHotkeys", "select", deselected.td);
                        for (const handler of handlers) {
                            handler({ $event: { selected: false } });
                        }
                    }
                },
                onCursorMoved: e => {
                    if (cursor) {
                        cursor.td.classList.remove("ehk-cell-cursor");
                    }
                    cursor = e.cursor;
                    cursor.td.classList.add("ehk-cell-cursor");
                }
            }));
        }

        function evaluateAttributeValues() {
            const freezeLeft = scope.$eval(attrs.freezeLeft);
            if (freezeLeft) {
                options.freezeLeft = Number.parseInt(freezeLeft);
            }
            const fixedHeaderTop = scope.$eval(attrs.fixedHeaderTop);
            if (fixedHeaderTop) {
                options.fixedHeaderTop = Number.parseInt(fixedHeaderTop);
            }

            const verticalScrollHeight = scope.$eval(attrs.verticalScrollHeight);
            if (verticalScrollHeight) {
                options.verticalScrollHeight = Number.parseInt(verticalScrollHeight);
            }
            options.scrollContainer = getVerticalScrollContainer(el, attrs);
        }

        // Deactivate the context when removing this directive from the DOM.
        scope.$on("destroy", () => {
            context.detach()
        })
    }

    function attachToContext(context) {
        context.attach();
        // Add apex-specific classes to the containers.
        context.verticalContainer.classList.add("apex-flex-table-container");
        context.horizontalContainer.classList.add("apex-freeze-columns-container");
    }

    function detachFromContext(context) {
        context.verticalContainer.classList.remove("apex-flex-table-container");
        context.horizontalContainer.classList.remove("apex-freeze-columns-container");
        context.detach()
    }
}

function getVerticalScrollContainer(el, attrs) {
    return attrs.verticalContainer
        ? $(el).closest(attrs.verticalContainer)[0]
        : window;
}
